import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import _ from "lodash";
import { useEffect, useMemo, useRef } from "react";
import ProposalField, { ProposalFieldCategory, ProposalFieldName } from "work/proposals/proposal-fields/proposal-field";

type IssueTraverserProps = {
  issueFields: ProposalField[];
  unreadCommentFields: ProposalField[];
  currentField: ProposalField | undefined;
  prevLabel?: string;
  nextLabel?: string;
  disabled?: boolean;
  onTraverseToNewField: (newField: ProposalField) => void;
};

export default function IssueTraverser(props: Readonly<IssueTraverserProps>) {
  const {
    issueFields,
    unreadCommentFields,
    currentField,
    prevLabel,
    nextLabel,
    disabled,
    onTraverseToNewField,
  } = props;

  const lastIndex = useRef<number>(0);
  const proposalFieldCategories = useMemo(() => Object.values(ProposalFieldCategory), []);
  const proposalFieldNames = useMemo(() => Object.values(ProposalFieldName), []);
  const fieldsPendingReview: ProposalField[] = useMemo(() => {
    const combined = [...unreadCommentFields, ...issueFields];
  
    // Remove duplicates and sort
    return _.chain(combined)
      .uniqBy((field) => `${field.category}-${field.name}-${field.id?.value ?? ''}`)
      .sortBy([
        (field) => proposalFieldCategories.indexOf(field.category),
        (field) => proposalFieldNames.indexOf(field.name)
      ])
      .value();
    }, [unreadCommentFields, issueFields, proposalFieldCategories, proposalFieldNames]);

  useEffect(() => initFirstPendingIssue(), []);

  const initFirstPendingIssue = (): void => {
    if (fieldsPendingReview.length > 0) {
      const firstField = fieldsPendingReview[0];
      onTraverseToNewField(firstField);
    }
  }

  const getPreviousFieldPendingReview = (): ProposalField | undefined => {
    if (fieldsPendingReview.length < 1) return;

    if(currentField){
      let currentIndex = fieldsPendingReview.findIndex((field) =>
        field.name === currentField.name &&
        (field.id ? field.id?.isEqualTo(currentField.id) : true)
      );

      if (currentIndex < 0) {
        currentIndex = lastIndex.current;
      } else {
        lastIndex.current = currentIndex;
      }

      let previousIndex = currentIndex - 1;

      if (previousIndex < 0) {
        previousIndex = fieldsPendingReview.length - 1;
      }

      return fieldsPendingReview[previousIndex];
    }
    else {
      return fieldsPendingReview.at(-1);
    }
  }

  const getNextFieldPendingReview = (): ProposalField | undefined => {
    if (fieldsPendingReview.length < 1) return;

    if(currentField){
      let currentIndex = fieldsPendingReview.findIndex((field) =>
        field.name === currentField.name &&
        (field.id ? field.id?.isEqualTo(currentField.id) : true)
      );

      if (currentIndex < 0) {
        currentIndex = lastIndex.current;
      } else {
        lastIndex.current = currentIndex;
      }

      let nextIndex = currentIndex + 1;

      if (nextIndex >= fieldsPendingReview.length) {
        nextIndex = 0;
      }

      return fieldsPendingReview[nextIndex];
    } else {
      return fieldsPendingReview[0];
    }
  }

  const previousField =  getPreviousFieldPendingReview();
  const nextField = getNextFieldPendingReview();

  return (
    <ButtonGroup
      disabled={disabled}
      size="small"
      color="primary"
      variant="outlined"
      orientation="horizontal"
      onMouseDown={(event) => event.preventDefault()}
    >
      <Button
        id="previous-issue"
        disabled={disabled || !previousField || previousField.isEqualTo(currentField)}
        sx={{ whiteSpace: 'nowrap' }}
        onClick={() => {
          if (previousField) onTraverseToNewField(previousField); 
        }}
      >
        <KeyboardArrowUpIcon />
        {prevLabel ?? "Prev Change"}
      </Button>
      <Button
        id="next-issue"
        disabled={disabled || !nextField || nextField.isEqualTo(currentField)}
        sx={{ whiteSpace: 'nowrap' }}
        onClick={() => {
          if (nextField) onTraverseToNewField(nextField);
        }}
      >
        {nextLabel ?? "Next Change"}
        <KeyboardArrowDownIcon />
      </Button>
    </ButtonGroup>
  );
}
